import React from 'react';
import {StandardMargin} from '../../../components/insights/invoicing/InvoicingPage.styled';
import {SubNavigation} from '@/web-components';
import {useIntl} from 'react-intl';
import {useHistory, useLocation} from 'react-router-dom';
import {TopHeaderBar, TopHeaderBarWrapper} from '../../shared/components/headers/top-header-bar/TopHeaderBar';
import {hasFeatureFlag} from '../../shared/util/FeatureUtil';
import {FEATURE_FLAGS} from '../../../constants';

// PIKA-558: these values are compared against the live pathname via startsWith() in
// findProjectSubPage(), so each has to be the post-redirect route registered in
// AdminRoutes.jsx. The single entry here previously read '/settings/priority-levels',
// which still navigates here fine (routerv2 redirects /settings/:subpath to
// /admin/:subpath) but never matches, so no tab was ever marked active.
// `testId` is pinned per entry rather than derived from `value`. It previously came from
// `value.toLowerCase().replaceAll('/', '-')`, so correcting the route above would silently
// have renamed both data-cy AND data-userpilot on the existing Priority Levels tab:
//   settings-projects-tabs-settings-priority-levels -> settings-projects-tabs-admin-project-priority
// No repo test matches either string, so CI would have stayed green -- but data-userpilot is
// consumed outside this repo, and any Userpilot guide anchored on the old value would have
// stopped matching after deploy with no failure signal. Priority Levels therefore keeps its
// original identifier verbatim, and the identifiers no longer move if a route changes again.
export const getProjectSubPages = () => [
	{
		value: '/admin/project-priority',
		label: 'settings.priority-levels',
		testId: 'settings-projects-tabs-settings-priority-levels',
	},
	{
		value: '/admin/project-task-monitoring',
		label: 'settings.task-monitoring',
		testId: 'settings-projects-tabs-admin-project-task-monitoring',
	},
	// Only while the feature flag is on for the company. The route in
	// AdminRoutes.jsx is gated on the same flag, so the two cannot disagree -- a tab that
	// rendered without its route would navigate to a blank page, and a route without its
	// tab would be unreachable except by typing the URL.
	...(hasFeatureFlag(FEATURE_FLAGS.CASCADE_DATES_FOR_DEPENDENCIES)
		? [
				{
					value: '/admin/project-task-dependencies',
					label: 'settings.task-dependencies',
					testId: 'settings-projects-tabs-admin-project-task-dependencies',
				},
		  ]
		: []),
];

export const findProjectSubPage = pathname => {
	return getProjectSubPages().find(subPage => pathname?.startsWith?.(subPage.value));
};

const SettingsProjectSubNav = () => {
	const {formatMessage} = useIntl();
	const {pathname} = useLocation();
	const history = useHistory();
	const activeProjectSubPage = findProjectSubPage(pathname);

	const handleSetActivePage = value => {
		const newPage = getProjectSubPages().some(option => option.value === value);
		if (activeProjectSubPage?.value !== value && newPage) {
			history.push(value);
		}
	};

	return (
		<>
			<TopHeaderBarWrapper>
				<TopHeaderBar title={formatMessage({id: 'common.projects'})} />
			</TopHeaderBarWrapper>
			<div style={{marginTop: '20px'}}>
				<StandardMargin data-cy={'Projects-tabs'}>
					{getProjectSubPages().map(option => (
						<SubNavigation.NavigationItem
							active={activeProjectSubPage?.value === option.value}
							onClick={() => handleSetActivePage(option.value)}
							key={option.value}
							cy={option.testId}
							userpilot={option.testId}
						>
							{formatMessage({id: option.label})}
						</SubNavigation.NavigationItem>
					))}
				</StandardMargin>
			</div>
		</>
	);
};

export default SettingsProjectSubNav;
